<template>
  <div class="learner-pics-episodes">
    <k-panel :hasContentToggle="false" class="linked-pics-episodes-panel sub-panel">
      <template #header>
        <h3 class="pics-sub-header"><i class="fa-regular fa-link" aria-hidden="true"></i> Linked PICS Episodes</h3>
      </template>
      <template #body>
        <div class="linked-pics-episodes-container">
          <p>All linked PICS episodes found for the learner.</p>
          <p v-if="$permissions.hasPermission('manage_ilr_mapping')">Use the table to re-assign episodes to the correct programme.</p>
          <p class="pics-episodes-count">{{ episodeCountMessage }}</p>
          <k-table
            :rows="linkedKableRows"
            class="pics-episodes-table"
            :headers="linkedKableHeaders"
            :hideable="true"
            :panel="false"
            :show-controls="true"
            :defaultHeaders="visibleHeaders"
          />
          </div>
        </template>
    </k-panel>
    <k-panel v-if="$permissions.hasPermission('manage_ilr_mapping')" :hasContentToggle="false" class="unlinked-pics-episodes-panel sub-panel">
      <template #header>
        <h3 class="pics-sub-header"><i class="fa-regular fa-link-slash" aria-hidden="true"></i> Unlinked PICS Episodes</h3>
      </template>
      <template #body>
        <div class="unlinked-pics-episodes-container">
          <div class="search-container">
            <p>Use the 'Search PICS' button to find unlinked episodes.</p>
            <button class="btn btn-primary"
              @click="searchForPicsEpisodes"
            >
              <i class="fas fa-search" aria-hidden="true"></i>
              Search PICS
            </button>
          </div>
          <div v-if="unlinkedEpisodes.length > 0" class="unlinked-pics-episodes-results">
            <p>Use the table to assign episodes to the correct programme.</p>
            <p class="pics-episodes-count">{{ unlinkedEpisodeCountMessage }}</p>
            <k-table
              :rows="unlinkedKableRows"
              class="pics-episodes-table"
              :headers="unlinkedKableHeaders"
              :panel="false"
              :show-controls="true"
              :defaultHeaders="visibleHeaders"
            />
          </div>
        </div>
      </template>
    </k-panel>
  </div>
</template>

<style scoped>
.linked-pics-episodes-panel {
  margin-top: 20px;
}

.linked-pics-episodes-container,
.unlinked-pics-episodes-container {
  display: flex;
  flex-direction: column;
  margin-top: 20px;
}

.learner-pics-episodes h3 {
  margin-top: 10px;
}

.pics-episodes-count {
  color: var(--kate-type-secondary) !important;
  margin-top: 10px;
}

.pics-unlinked-episodes-count {
  color: var(--kate-type-secondary) !important;
  margin-top: 20px;
}

.search-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';

export default {
  components: {
    KTable,
    KPanel,
  },

  mixins: [TimeMixin],

  props: {
    picsEpisodes: {
      type: Array,
      required: true,
    },
    learnerInfo: {
      type: Object,
      required: true,
    },
    unlinkedEpisodes: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  computed: {
    episodeCountMessage() {
      if (this.picsEpisodes.length === 0) {
        return 'No PICS episodes found';
      }
      if (this.picsEpisodes.length === 1) {
        return '1 PICS episode found';
      }
      return `${this.picsEpisodes.length} PICS episodes found`;
    },

    unlinkedEpisodeCountMessage() {
      if (this.unlinkedEpisodes.length === 0) {
        return 'No unlinked PICS episodes found';
      }
      if (this.unlinkedEpisodes.length === 1) {
        return '1 unlinked PICS episode found';
      }
      return `${this.unlinkedEpisodes.length} unlinked PICS episodes found`;
    },

    linkedKableHeaders() {
      const headers = {
        episode_id: {
          name: 'Episode ID',
          type: 'string',
          sortable: true,
        },
        uln: {
          name: 'ULN',
          type: 'string',
          sortable: true,
        },
        full_name: {
          name: 'Learner Name',
          type: 'string',
          sortable: true,
        },
        programme: {
          name: 'Programme',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        product: {
          name: 'Product',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        ilr_status: {
          name: 'Status',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        start_date: {
          name: 'Start Date',
          type: 'string',
          sortable: true,
        },
        end_date: {
          name: 'End Date',
          type: 'string',
          sortable: true,
        },
      };
      if (this.$permissions.hasPermission('manage_ilr_mapping')) {
        headers.reassign_episode = {
          name: 'Re-assign Episode',
          type: 'button',
          options: {
            preset: 'edit',
            ariaLabel: 'Re-assign Episode',
            title: 'Re-assign Episode',
            callback: this.reassignEpisode,
          },
        };
        headers.unassign_episode = {
          name: 'Unassign Episode',
          type: 'button',
          options: {
            preset: 'delete',
            ariaLabel: 'Unassign Episode',
            title: 'Unassign Episode',
            callback: this.unassignEpisode,
          },
        };
      }
      return headers;
    },

    unlinkedKableHeaders() {
      return {
        episode_id: {
          name: 'Episode ID',
          type: 'string',
          sortable: true,
        },
        uln: {
          name: 'ULN',
          type: 'string',
          sortable: true,
        },
        full_name: {
          name: 'Learner Name',
          type: 'string',
          sortable: true,
        },
        email: {
          name: 'Email',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        standard: {
          name: 'Standard',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        ilr_status: {
          name: 'Status',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        start_date: {
          name: 'Start Date',
          type: 'string',
          sortable: true,
        },
        end_date: {
          name: 'End Date',
          type: 'string',
          sortable: true,
        },
        assign_episode: {
          name: 'Assign Episode',
          type: 'button',
          options: {
            preset: 'edit',
            ariaLabel: 'Assign Episode',
            title: 'Assign Episode',
            callback: this.assignEpisode,
          },
        },
      };
    },

    visibleHeaders() {
      return ['uln', 'full_name', 'email', 'programme', 'product', 'standard', 'ilr_status', 'start_date', 'end_date', 'reassign_episode', 'assign_episode', 'unassign_episode'];
    },

    linkedKableRows() {
      if (!this.picsEpisodes || this.picsEpisodes.length === 0) {
        return [];
      }
      return this.picsEpisodes.map(episode => ({
        episode_id: episode.pics_aim_episode_id,
        uln: episode.uln,
        full_name: episode.pics_full_name,
        ilr_status: episode.ilr_status,
        start_date: this.formatDateString(episode.ilr_start_date),
        end_date: this.formatDateString(episode.ilr_planned_end_date),
        product: episode.product_name,
        programme: episode.programme_name,
        programme_member_id: episode.programme_member_id,
        pics_id: episode.id,
        lars_code: episode.ilr_standard_lars_code,
      }));
    },

    unlinkedKableRows() {
      if (!this.unlinkedEpisodes || this.unlinkedEpisodes.length === 0) {
        return [];
      }
      return this.unlinkedEpisodes.map(episode => ({
        episode_id: episode.pics_aim_episode_id,
        uln: episode.uln,
        full_name: episode.pics_full_name,
        ilr_status: episode.ilr_status,
        start_date: this.formatDateString(episode.ilr_start_date),
        end_date: this.formatDateString(episode.ilr_planned_end_date),
        standard: episode.ilr_standard,
        programme: episode.programme_name,
        programme_member_id: episode.programme_member_id,
        pics_id: episode.pics_id,
        lars_code: episode.ilr_standard_lars_code,
        email: episode.pics_email,
        candidate_programmes: episode.candidate_programmes,
      }));
    },
  },

  methods: {
    reassignEpisode(key, index, row) {
      this.$emit('reassign-episode', row);
    },

    unassignEpisode(key, index, row) {
      this.$emit('unassign-episode', row);
    },

    assignEpisode(key, index, row) {
      this.$emit('assign-episode', row);
    },

    searchForPicsEpisodes() {
      this.$emit('search-pics');
    },
  },
};
</script>
